import React, { useRef, useState } from 'react';
import {
  C,
  imgUrl,
  normalizeImgField,
  nx,
  ny,
  resolveFeatureIconAndLabel,
  SLOT_REELS_COL_GAP,
  SLOT_REELS_COL_W,
  SLOT_REELS_HEADER_H,
  SLOT_REELS_ITEM_GAP,
  SLOT_REELS_ITEM_H,
  SLOT_REELS_NODE_PADDING,
} from '../flowCanvas.constants';

const BORDER_R = 8;
const HEADER_H = 32;
const PANEL_HEADER_H = 56;
const PANEL_RADIUS = 10;
const PANEL_INSET = 12;
const ITEM_INSET = 10;
const IMAGE_PAD = 12;
const IMAGE_H = 92;
const TOGGLE_W = 32;
const TOGGLE_H = 18;
const DELETE_R = 10;
const DELETE_FILL = '#dc2626';
const AVAIL_PILL_W = 42;
const AVAIL_PILL_H = 20;
const CHANCE_PILL_W = 52;
const COUNT_ROW_GAP = 8;
const TRIGGER_SLOT_H = 42;
const TRIGGER_DELETE_R = 8;
const OPTIN_INFO_H = 86;
const ADD_BTN_R = 11;
const ADD_BTN_LABEL_Y_GAP = 9;
const ADD_SECTION_H = 50;
const ADD_SECTION_GAP = 18;
const ADD_SECTION_BOTTOM_PAD = 14;
const TRIGGER_DRAG_FILL = 'rgba(145,70,255,0.12)';
const TRIGGER_DRAG_STROKE = '#c060ff';
const TRIGGER_REPLACE_FILL = 'rgba(220,38,38,0.12)';
const TRIGGER_REPLACE_STROKE = '#f87171';

const truncate = (value, max = 14) => {
  const str = String(value || '').trim();
  if (!str) return 'Empty';
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

const getSlotSettings = (step, liveCustomisationData) => {
  const liveVals = liveCustomisationData?.setup?.globalSettings?.slotSettings;
  const source = Array.isArray(liveVals)
    ? liveVals
    : (step?.populatedData?.variables?.[0]?.allValues || []);
  return source?.[0] || {};
};

const getReelItems = (liveCustomisationData, reelIndex) => {
  const groupName = `options${reelIndex + 1}`;
  const liveVals = liveCustomisationData?.setup?.[groupName]?.options;
  return Array.isArray(liveVals) ? liveVals.filter(item => item != null) : [];
};

const getItemCount = (item) => Math.max(0, Number(item?.amountAvailable) || 0);

const getReelTotalCount = (items) => items.reduce((sum, item) => sum + getItemCount(item), 0);

const getItemChance = (item, reelTotalCount) => {
  if (!reelTotalCount) return 0;
  return Math.round((getItemCount(item) / reelTotalCount) * 100);
};

const getChanceHue = (chancePct) => {
  const clamped = Math.max(0, Math.min(100, Number(chancePct) || 0));
  return Math.round((clamped / 100) * 120);
};

const getChanceFill = (chancePct) => `hsla(${getChanceHue(chancePct)}, 72%, 42%, 0.22)`;

const getChanceStroke = (chancePct) => `hsl(${getChanceHue(chancePct)}, 72%, var(--fc-chance-stroke-l))`;

const isOptInReel = (slotSettings, reelIndex) => !!slotSettings?.[`useOptInList_reel${reelIndex + 1}`];

const getNodeHeight = (step, liveCustomisationData) => {
  const slotSettings = getSlotSettings(step, liveCustomisationData);
  const reelCount = Math.max(1, Number(slotSettings?.reels) || 3);
  let maxPanelContentH = 56;
  for (let index = 0; index < Math.min(reelCount, 5); index += 1) {
    if (isOptInReel(slotSettings, index)) {
      maxPanelContentH = Math.max(maxPanelContentH, OPTIN_INFO_H);
      continue;
    }
    const itemCount = Math.max(getReelItems(liveCustomisationData, index).length || 1, 1);
    const itemsH = itemCount * (SLOT_REELS_ITEM_H + SLOT_REELS_ITEM_GAP) - SLOT_REELS_ITEM_GAP;
    const panelContentH = itemsH + ADD_SECTION_GAP + ADD_SECTION_H;
    maxPanelContentH = Math.max(maxPanelContentH, panelContentH);
  }
  return SLOT_REELS_HEADER_H + PANEL_HEADER_H + maxPanelContentH + 24;
};

const getNodeWidth = (reelCount) => {
  const count = Math.max(1, Math.min(5, Number(reelCount) || 3));
  return SLOT_REELS_NODE_PADDING * 2 + count * SLOT_REELS_COL_W + (count - 1) * SLOT_REELS_COL_GAP;
};

const SlotReelsNode = ({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  campaignID,
  campaignFeatures,
  liveCustomisationData,
  activeGraphicIndex,
  onGraphicClick,
  onFlowFieldChange,
  onSlotOptionDisruptorDrop,
  onSlotOptionDisruptorRemove,
  onSlotOptionRemove,
}) => {
  const [dragOverSlotKey, setDragOverSlotKey] = useState(null);
  const dragDepthBySlotRef = useRef({});
  const x = overrideX ?? nx(step);
  const y = overrideY ?? ny(step, centreY);
  const slotSettings = getSlotSettings(step, liveCustomisationData);
  const reelCount = Math.max(1, Number(slotSettings?.reels) || 3);
  const visibleReelCount = Math.min(reelCount, 5);
  const nodeW = getNodeWidth(visibleReelCount);
  const nodeH = getNodeHeight(step, liveCustomisationData);
  const topY = y - nodeH / 2;
  const leftX = x - nodeW / 2;
  const panelStartX = leftX + SLOT_REELS_NODE_PADDING;
  const panelY = topY + SLOT_REELS_HEADER_H + 10;
  const panelH = nodeH - SLOT_REELS_HEADER_H - 18;

  return (
    <g onClick={onClick} style={{ cursor: 'pointer', fontFamily: 'inherit' }}>
      <rect
        x={leftX}
        y={topY}
        width={nodeW}
        height={nodeH}
        rx={BORDER_R}
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.border}
        strokeWidth={selected ? 2 : 1.5}
      />

      <clipPath id={`slot-reels-hdr-${step.stepId}`}>
        <rect x={leftX} y={topY} width={nodeW} height={HEADER_H} rx={BORDER_R} ry={BORDER_R} />
      </clipPath>
      <rect
        x={leftX}
        y={topY}
        width={nodeW}
        height={HEADER_H + BORDER_R}
        clipPath={`url(#slot-reels-hdr-${step.stepId})`}
        fill="rgba(145,70,255,0.22)"
      />

      <text x={leftX + 14} y={topY + 21} fill={C.text} className="bold-text">
        Reel Images
      </text>

      {Array.from({ length: visibleReelCount }).map((_, reelIndex) => {
        const reelItems = getReelItems(liveCustomisationData, reelIndex);
        const reelTotalCount = getReelTotalCount(reelItems);
        const panelX = panelStartX + reelIndex * (SLOT_REELS_COL_W + SLOT_REELS_COL_GAP);
        const toggleVarName = `useOptInList_reel${reelIndex + 1}`;
        const optedIn = isOptInReel(slotSettings, reelIndex);
        const isPanelActive = activeGraphicIndex && typeof activeGraphicIndex === 'object'
          && activeGraphicIndex.reelIndex === reelIndex
          && (activeGraphicIndex.itemIndex === null || activeGraphicIndex.itemIndex === undefined);

        return (
          <g
            key={reelIndex}
            onClick={(event) => {
              event.stopPropagation();
              onGraphicClick?.({ reelIndex, itemIndex: null });
            }}
          >
            <rect
              x={panelX}
              y={panelY}
              width={SLOT_REELS_COL_W}
              height={panelH}
              rx={PANEL_RADIUS}
              fill="rgba(17,21,38,0.78)"
              stroke={isPanelActive ? C.sel : C.border}
              strokeWidth={isPanelActive ? 1.6 : 1}
            />

            <text x={panelX + 10} y={panelY + 18} fill={C.text} className="bold-text">
              {`Reel ${reelIndex + 1}`}
            </text>

            <text x={panelX + 10} y={panelY + 34} fill={C.textMuted} className="small-text bold-text">
              Opt-In
            </text>
            <g
              onClick={(event) => {
                event.stopPropagation();
                onFlowFieldChange?.('setup', 'globalSettings', 'slotSettings', 0, toggleVarName, !optedIn);
                onClick?.();
              }}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={panelX + SLOT_REELS_COL_W - TOGGLE_W - 10}
                y={panelY + 22}
                width={TOGGLE_W}
                height={TOGGLE_H}
                rx={TOGGLE_H / 2}
                fill={optedIn ? 'rgba(145,70,255,0.70)' : C.circle}
                stroke={optedIn ? C.purple : C.border}
                strokeWidth="1"
              />
              <circle
                cx={panelX + SLOT_REELS_COL_W - TOGGLE_W - 10 + (optedIn ? TOGGLE_W - TOGGLE_H / 2 : TOGGLE_H / 2)}
                cy={panelY + 31}
                r="7"
                fill={optedIn ? '#fff' : C.border}
              />
            </g>

            {optedIn ? (
              <g>
                <rect
                  x={panelX + PANEL_INSET}
                  y={panelY + PANEL_HEADER_H}
                  width={SLOT_REELS_COL_W - PANEL_INSET * 2}
                  height={OPTIN_INFO_H}
                  rx="8"
                  fill="rgba(145,70,255,0.08)"
                  stroke="rgba(145,70,255,0.30)"
                  strokeDasharray="4 3"
                />
                <text x={panelX + SLOT_REELS_COL_W / 2} y={panelY + PANEL_HEADER_H + 28} fill={C.text} className="bold-text" textAnchor="middle">
                  Uses chat participant names
                </text>
                <text x={panelX + SLOT_REELS_COL_W / 2} y={panelY + PANEL_HEADER_H + 47} fill={C.textMuted} className="small-text" textAnchor="middle">
                  This reel fills from chat opt-ins
                </text>
                <text x={panelX + SLOT_REELS_COL_W / 2} y={panelY + PANEL_HEADER_H + 64} fill={C.textMuted} className="small-text" textAnchor="middle">
                  rather than custom image entries
                </text>
              </g>
            ) : (
              <>
                {(reelItems.length > 0 ? reelItems : [{}]).map((item, itemIndex) => {
              const itemY = panelY + PANEL_HEADER_H + itemIndex * (SLOT_REELS_ITEM_H + SLOT_REELS_ITEM_GAP);
              const itemX = panelX + PANEL_INSET;
              const itemW = SLOT_REELS_COL_W - PANEL_INSET * 2;
              const imageY = itemY + 28;
              const imageW = itemW - IMAGE_PAD * 2;
              const countRowW = AVAIL_PILL_W + COUNT_ROW_GAP + CHANCE_PILL_W;
              const countRowX = itemX + (itemW - countRowW) / 2;
              const amountPillX = countRowX;
              const chancePillX = countRowX + AVAIL_PILL_W + COUNT_ROW_GAP;
              const chancePct = getItemChance(item, reelTotalCount);
              const triggerLabelY = itemY + 166;
              const triggerSlotY = itemY + 176;
              const slotKey = `${reelIndex}-${itemIndex}`;
              const checkerId = `slot-reels-checker-${step.stepId}-${reelIndex}-${itemIndex}`;
              const clipId = `slot-reels-clip-${step.stepId}-${reelIndex}-${itemIndex}`;
              const imageField = normalizeImgField(item?.image);
              const imageSrc = imageField.srcUrl || imgUrl(imageField.name, imageField.isDefault, step?.featureInfo?.defaultAssetFolder || 'randomiser', campaignID);
              const triggerId = item?.disruptor || '';
              const triggerMeta = resolveFeatureIconAndLabel(triggerId, campaignFeatures);
              const isSlotDragOver = dragOverSlotKey === slotKey;
              const triggerFill = isSlotDragOver
                ? (triggerId ? TRIGGER_REPLACE_FILL : TRIGGER_DRAG_FILL)
                : (triggerId ? C.circle : C.circle);
              const triggerStroke = isSlotDragOver
                ? (triggerId ? TRIGGER_REPLACE_STROKE : TRIGGER_DRAG_STROKE)
                : (triggerId ? C.purple : C.border);
              const triggerStrokeWidth = isSlotDragOver ? 1.5 : (triggerId ? 1.2 : 1);
              const triggerHint = isSlotDragOver
                ? (triggerId ? 'Drop to replace' : 'Release to attach')
                : (triggerId ? truncate(triggerMeta.label, 16) : 'Drag a component here');
              const isItemActive = activeGraphicIndex && typeof activeGraphicIndex === 'object'
                && activeGraphicIndex.reelIndex === reelIndex
                && activeGraphicIndex.itemIndex === itemIndex;

              return (
                <g
                  key={`${reelIndex}-${itemIndex}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onGraphicClick?.({ reelIndex, itemIndex });
                  }}
                >
                  <rect
                    x={itemX}
                    y={itemY}
                    width={itemW}
                    height={SLOT_REELS_ITEM_H}
                    rx="8"
                    fill={C.circle}
                    stroke={isItemActive ? C.sel : (triggerId ? C.purple : C.border)}
                    strokeWidth={isItemActive ? 1.6 : (triggerId ? 1.2 : 1)}
                  />

                  <defs>
                    <clipPath id={clipId}>
                      <rect x={itemX + IMAGE_PAD} y={imageY} width={imageW} height={IMAGE_H} rx="8" />
                    </clipPath>
                    <pattern id={checkerId} patternUnits="userSpaceOnUse" width="8" height="8">
                      <rect width="8" height="8" fill="#555" />
                      <rect width="4" height="4" fill="#333" />
                      <rect x="4" y="4" width="4" height="4" fill="#333" />
                    </pattern>
                  </defs>

                  <text x={itemX + itemW / 2} y={itemY + 18} textAnchor="middle" fill={C.text} className="bold-text">
                    {truncate(item?.name || 'Empty', 20)}
                  </text>

                  <rect
                    x={itemX + IMAGE_PAD}
                    y={imageY}
                    width={imageW}
                    height={IMAGE_H}
                    rx="8"
                    fill={`url(#${checkerId})`}
                  />
                  {imageSrc ? (
                    <image
                      href={imageSrc}
                      x={itemX + IMAGE_PAD}
                      y={imageY}
                      width={imageW}
                      height={IMAGE_H}
                      preserveAspectRatio="xMidYMid meet"
                      clipPath={`url(#${clipId})`}
                    />
                  ) : (
                    <>
                      <rect
                        x={itemX + IMAGE_PAD}
                        y={imageY}
                        width={imageW}
                        height={IMAGE_H}
                        rx="8"
                        fill="none"
                        stroke={C.border}
                        strokeDasharray="4 3"
                      />
                      <text x={itemX + itemW / 2} y={imageY + IMAGE_H / 2 + 4} textAnchor="middle" fill={C.textDim} className="small-text">
                        No image
                      </text>
                    </>
                  )}

                  <rect
                    x={amountPillX}
                    y={itemY + 126}
                    width={AVAIL_PILL_W}
                    height={AVAIL_PILL_H}
                    rx={AVAIL_PILL_H / 2}
                    fill="rgba(145,70,255,0.16)"
                    stroke="rgba(145,70,255,0.34)"
                    strokeWidth="1"
                  />
                  <text
                    x={amountPillX + AVAIL_PILL_W / 2}
                    y={itemY + 139}
                    textAnchor="middle"
                    fill={C.text}
                    className="small-text bold-text"
                  >
                    {`x${item?.amountAvailable ?? 0}`}
                  </text>
                  <rect
                    x={chancePillX}
                    y={itemY + 126}
                    width={CHANCE_PILL_W}
                    height={AVAIL_PILL_H}
                    rx={AVAIL_PILL_H / 2}
                    fill={getChanceFill(chancePct)}
                    stroke={getChanceStroke(chancePct)}
                    strokeWidth="1"
                  />
                  <text
                    x={chancePillX + CHANCE_PILL_W / 2}
                    y={itemY + 139}
                    textAnchor="middle"
                    fill={C.text}
                    className="small-text bold-text"
                  >
                    {`${chancePct}%`}
                  </text>

                  <text x={itemX + itemW / 2} y={triggerLabelY} textAnchor="middle" fill={C.textMuted} className="small-text bold-text">
                    Item Trigger
                  </text>

                  <g
                    onClick={(event) => event.stopPropagation()}
                  >
                    <rect
                      x={itemX + ITEM_INSET}
                      y={triggerSlotY}
                      width={itemW - ITEM_INSET * 2}
                      height={TRIGGER_SLOT_H}
                      rx="8"
                      fill={triggerFill}
                      stroke={triggerStroke}
                      strokeWidth={triggerStrokeWidth}
                      strokeDasharray={triggerId ? 'none' : '4 3'}
                      onDragEnter={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        dragDepthBySlotRef.current[slotKey] = (dragDepthBySlotRef.current[slotKey] || 0) + 1;
                        setDragOverSlotKey(slotKey);
                      }}
                      onDragLeave={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const nextDepth = Math.max((dragDepthBySlotRef.current[slotKey] || 1) - 1, 0);
                        if (nextDepth === 0) {
                          delete dragDepthBySlotRef.current[slotKey];
                          setDragOverSlotKey((current) => (current === slotKey ? null : current));
                          return;
                        }
                        dragDepthBySlotRef.current[slotKey] = nextDepth;
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (dragOverSlotKey !== slotKey) setDragOverSlotKey(slotKey);
                      }}
                      onDrop={(event) => {
                        delete dragDepthBySlotRef.current[slotKey];
                        setDragOverSlotKey((current) => (current === slotKey ? null : current));
                        onSlotOptionDisruptorDrop?.(reelIndex, itemIndex, event);
                      }}
                    />
                    {triggerId && !isSlotDragOver ? (
                      <g style={{ pointerEvents: 'none' }}>
                        <circle
                          cx={itemX + ITEM_INSET + 16}
                          cy={triggerSlotY + TRIGGER_SLOT_H / 2}
                          r={11}
                          fill="rgba(145,70,255,0.15)"
                          stroke="rgba(145,70,255,0.35)"
                          strokeWidth="1"
                        />
                        <text x={itemX + ITEM_INSET + 16} y={triggerSlotY + TRIGGER_SLOT_H / 2 + 4} textAnchor="middle" className="small-text">
                          {triggerMeta.icon}
                        </text>
                        <text x={itemX + ITEM_INSET + 32} y={triggerSlotY + TRIGGER_SLOT_H / 2 + 4} fill={C.text} className="small-text bold-text">
                          {triggerHint}
                        </text>
                      </g>
                    ) : (
                      <text
                        x={itemX + itemW / 2}
                        y={triggerSlotY + TRIGGER_SLOT_H / 2 + 4}
                        fill={isSlotDragOver ? C.text : C.textDim}
                        className="small-text"
                        textAnchor="middle"
                        style={{ pointerEvents: 'none' }}
                      >
                        {triggerHint}
                      </text>
                    )}
                  </g>

                  <g onClick={(event) => {
                    event.stopPropagation();
                    onSlotOptionRemove?.(reelIndex, itemIndex);
                  }} style={{ cursor: 'pointer' }}>
                    <circle cx={itemX + itemW - 14} cy={itemY + 14} r={DELETE_R} fill={DELETE_FILL} />
                    <g transform={`translate(${itemX + itemW - 14}, ${itemY + 14})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                      <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                      <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                      <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                      <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                      <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                    </g>
                  </g>
                  {triggerId ? (
                    <g onClick={(event) => {
                      event.stopPropagation();
                      onSlotOptionDisruptorRemove?.(reelIndex, itemIndex);
                    }} style={{ cursor: 'pointer' }}>
                      <circle
                        cx={itemX + itemW - ITEM_INSET - 14}
                        cy={triggerSlotY + 14}
                        r={TRIGGER_DELETE_R}
                        fill={DELETE_FILL}
                      />
                      <g
                        transform={`translate(${itemX + itemW - ITEM_INSET - 14}, ${triggerSlotY + 14}) scale(0.8)`}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                        <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                        <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                        <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                        <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                      </g>
                    </g>
                  ) : null}
                </g>
                );
              })}

                <g
                  onClick={(event) => {
                    event.stopPropagation();
                    onGraphicClick?.({ reelIndex, itemIndex: null, addItem: true });
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {(() => {
                    const visibleItemCount = Math.max(reelItems.length || 1, 1);
                    const itemsH = visibleItemCount * (SLOT_REELS_ITEM_H + SLOT_REELS_ITEM_GAP) - SLOT_REELS_ITEM_GAP;
                    const addCenterX = panelX + SLOT_REELS_COL_W / 2;
                    const addCenterY = panelY + PANEL_HEADER_H + itemsH + ADD_SECTION_GAP + ADD_SECTION_H - ADD_SECTION_BOTTOM_PAD - ADD_BTN_R;
                    return (
                      <>
                        <text
                          x={addCenterX}
                          y={addCenterY - ADD_BTN_R - ADD_BTN_LABEL_Y_GAP}
                          textAnchor="middle"
                          className="small-text bold-text"
                          fill={C.greenText}
                          style={{ userSelect: 'none' }}
                        >
                          Add Image
                        </text>
                        <circle cx={addCenterX} cy={addCenterY} r={ADD_BTN_R} fill={C.greenText} />
                        <text
                          x={addCenterX}
                          y={addCenterY + 1}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="16"
                          fontWeight="300"
                          fill="#fff"
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          +
                        </text>
                      </>
                    );
                  })()}
                </g>
              </>
            )}
          </g>
        );
      })}
    </g>
  );
};

export default SlotReelsNode;